import React from 'react';
import { observable, action } from 'mobx';
import AdminOverview from './AdminOverview';
import styled from 'styled-components';

// ── Light theme colors ──
const COLORS = {
    bg: '#f5f6f7',
    accent: '#2185d0',
    text: '#1a1a2e',
    border: '#e2e5e9',
    weekend: '#f0f0f0',
    today: '#2185d0',
    rowAlt: '#fafbfc',
    label: '#6b7280',
};

// ── Styled components ──
const TimelineWrapper = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    background: ${COLORS.bg};
    overflow: hidden;
    position: relative;
`;

const TimelineToolbar = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border-bottom: 1px solid ${COLORS.border};
    background: #fff;
    flex-shrink: 0;
    flex-wrap: wrap;
`;

const TBtn = styled.button`
    padding: 4px 12px;
    border: 1px solid ${COLORS.border};
    border-radius: 4px;
    background: #fff;
    color: ${COLORS.text};
    cursor: pointer;
    font-size: 13px;
    &:hover { background: ${COLORS.accent}; color: #fff; border-color: ${COLORS.accent}; }
`;

const ZoomLabel = styled.span`
    font-size: 12px;
    color: ${COLORS.label};
    min-width: 80px;
    text-align: center;
`;

const WeekLabel = styled.span`
    font-size: 13px;
    font-weight: 500;
    color: ${COLORS.text};
    min-width: 140px;
    text-align: center;
`;

const TimelineBody = styled.div`
    display: flex;
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
`;

const LabelColumn = styled.div`
    width: 240px;
    margin-top: 49px;
    flex-shrink: 0;
    overflow-y: auto;
    overflow-x: hidden;
    border-right: 1px solid ${COLORS.border};
    background: #fff;
`;

const GroupHeader = styled.div`
    padding: 8px 12px;
    font-weight: 600;
    font-size: 12px;
    height: 30px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${COLORS.label};
    background: ${COLORS.bg};
    border-bottom: 1px solid ${COLORS.border};
    cursor: ${props => props.clickable ? 'pointer' : 'default'};
    &:hover { background: #eef0f2; }
`;

const LabelRow = styled.div`
    display: flex;
    height: 32px;
    align-items: center;
    gap: 8px;
    padding: 6px 12px;
    border-bottom: 1px solid ${COLORS.border};
    cursor: pointer;
    &:hover { background: ${COLORS.bg}; }
    &:nth-child(even) { background: ${COLORS.rowAlt}; }
`;

const LabelColor = styled.div`
    width: 10px;
    height: 10px;
    border-radius: 2px;
    flex-shrink: 0;
`;

const LabelText = styled.div`
    flex: 1 1 auto;
    font-size: 13px;
    color: ${COLORS.text};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const TimelineScroll = styled.div`
    flex: 1 1 auto;
    overflow: auto;
    position: relative;
`;

const TimelineDates = styled.div`
    display: flex;
    border-bottom: 1px solid ${COLORS.border};
    position: sticky;
    top: 0;
    z-index: 2;
    background: #fff;
`;

const DateCell = styled.div`
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 4px 0;
    border-right: 1px solid ${COLORS.border};
    background: ${props => props.weekend ? COLORS.weekend : '#fff'};
`;

const DateMonth = styled.span`
    font-size: 10px;
    color: ${COLORS.label};
`;

const DateDay = styled.span`
    font-size: 14px;
    font-weight: 500;
    color: ${props => props.today ? COLORS.accent : COLORS.text};
`;

const TimelineGrid = styled.div`
    position: absolute;
    top: 0;
    bottom: 0;
    pointer-events: none;
`;

const GridLine = styled.div`
    position: absolute;
    top: 0;
    bottom: 0;
    width: 1px;
    background: ${COLORS.border};
`;

const TodayLine = styled.div`
    position: absolute;
    top: 0;
    bottom: 0;
    width: 2px;
    background: ${COLORS.today};
    z-index: 1;
    opacity: 0.5;
`;

const GroupTrack = styled.div`
    border-bottom: 2px solid ${COLORS.bg};
`;

const TrackLabel = styled.div`
    padding: 4px 12px;
    font-size: 11px;
    font-weight: 600;
    color: ${COLORS.label};
    background: ${COLORS.bg};
    border-bottom: 1px solid ${COLORS.border};
`;

const BarRow = styled.div`
    position: relative;
    height: 32px;
    border-bottom: 1px solid ${COLORS.border};
    &:nth-child(even) { background: ${COLORS.rowAlt}; }
`;

const Bar = styled.div`
    position: absolute;
    top: 4px;
    height: 24px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    padding: 0 8px;
    font-size: 11px;
    color: #fff;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
    transition: box-shadow 0.15s;
    &:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.2); z-index: 1; }
    &:hover .bar-edit-btn { display: flex; }
`;

const GroupHeaderFlex = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

const LoadingMsg = styled.div`
    padding: 40px;
    text-align: center;
    color: ${COLORS.label};
`;

// ── Date helpers (reused from CapacityPlanner types) ──
export function getStartOfWeek(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff); d.setHours(0, 0, 0, 0);
    return d;
}

export function addDays(date: Date, days: number): Date {
    const d = new Date(date); d.setDate(d.getDate() + days); return d;
}

export function daysBetween(d1: Date, d2: Date): number {
    const a = new Date(d1); a.setHours(0, 0, 0, 0);
    const b = new Date(d2); b.setHours(0, 0, 0, 0);
    return Math.round((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24));
}

export function isToday(date: Date): boolean {
    return new Date(date).toDateString() === new Date().toDateString();
}

export function isWeekend(date: Date): boolean {
    const d = new Date(date).getDay();
    return d === 0 || d === 6;
}

// ── Types for timeline groups ──
export interface TimelineGroup {
    id: string | number;
    label: string;
    items: any[];
    color?: string;
}

// ── TimelineAdminOverview base class ──
export default class TimelineAdminOverview extends AdminOverview {
    // Don't render the default table
    settings = [];
    buttons = [];
    toolbar = [];

    // ── Timeline state (R-005) ──
    @observable dayWidth = 40;
    visibleWeeks = 16;
    startDate = getStartOfWeek(new Date());
    @observable timelineOffset = 0;

    // ── Subclass hooks (R-004) ──
    // Override these in subclasses
    get timelineGroups(): TimelineGroup[] { return []; }
    getBarStart(item: any): Date | null { void item; return null; }
    getBarEnd(item: any): Date | null { void item; return null; }
    barColor(item: any): string { void item; return COLORS.accent; }
    renderTimelineBar(item: any): React.ReactNode { void item; return null; }
    onBarClick(item: any): void { void item; /* no-op */ }
    onBarDoubleClick(item: any): void { void item; /* no-op */ }
    onLabelDoubleClick(group: TimelineGroup): void { void group; /* no-op */ }

    // ── Edit hooks (subclasses can override) ──
    // Return a React node for the edit button on a bar, or null to disable
    renderBarEditButton(item: any): React.ReactNode { void item; return null; }
    // Return a React node for the edit button on a group header, or null to disable
    renderGroupEditButton(group: TimelineGroup): React.ReactNode { void group; return null; }
    // Return a React node for the add button in the toolbar, or null to disable
    renderAddButton(): React.ReactNode { return null; }
    // Return a React node for a back button in the toolbar, or null to disable
    renderBackButton(): React.ReactNode { return null; }
    // The modal element to render (controlled by subclass state)
    renderEditModal(): React.ReactNode { return null; }
    renderSidePanel(): React.ReactNode { return null; }

    // ── Timeline helpers (R-006) ──
    getTimelineDates(): Date[] {
        const totalDays = this.visibleWeeks * 7;
        const dates: Date[] = [];
        for (let i = 0; i < totalDays; i++) dates.push(addDays(this.startDate, i));
        return dates;
    }

    daysBetween(d1: Date, d2: Date): number { return daysBetween(d1, d2); }

    @action prevWeek = () => {
        this.timelineOffset--;
        this.startDate = addDays(getStartOfWeek(new Date()), this.timelineOffset * 7);
    };
    @action nextWeek = () => {
        this.timelineOffset++;
        this.startDate = addDays(getStartOfWeek(new Date()), this.timelineOffset * 7);
    };
    @action goToCurrentWeek = () => {
        this.timelineOffset = 0;
        this.startDate = getStartOfWeek(new Date());
    };

    @action zoomIn = () => { if (this.dayWidth < 80) this.dayWidth += 10; };
    @action zoomOut = () => { if (this.dayWidth > 20) this.dayWidth -= 10; };
    get zoomLabel(): string {
        if (this.dayWidth < 30) return 'Month View';
        if (this.dayWidth < 50) return 'Week View';
        return 'Day View';
    }

    get currentWeekLabel(): string {
        const end = addDays(this.startDate, this.visibleWeeks * 7 - 1);
        return `${this.startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    }

    get timelineEnd(): Date {
        return addDays(this.startDate, this.visibleWeeks * 7 - 1);
    }

    // Check if a date range overlaps with the visible timeline window
    isInRange(start: Date | null, end: Date | null): boolean {
        if (!start && !end) return true; // no dates = always show
        const visStart = this.startDate;
        const visEnd = this.timelineEnd;
        const s = start || visStart;
        const e = end || visEnd;
        return s <= visEnd && e >= visStart;
    }

    scrollToToday = () => {
        const scroll = document.getElementById('timeline-scroll-area');
        if (!scroll) return;
        scroll.scrollLeft = daysBetween(this.startDate, new Date()) * this.dayWidth - scroll.clientWidth / 2;
    };

    // ── Override renderContent (R-002) ──
    // We keep the sidebar filters and my-filters from AdminOverview,
    // but replace the table with a timeline.
    renderContent() {
        const dates = this.getTimelineDates();
        const width = this.dayWidth;
        const totalWidth = dates.length * width;
        const groups = this.timelineGroups;

        return (
            <React.Fragment>
                <TimelineWrapper>
                    {/* Toolbar with week nav + zoom (R-006) */}
                    <TimelineToolbar>
                        {this.renderBackButton()}
                        <TBtn data-test-timeline-prev-week onClick={this.prevWeek}>‹</TBtn>
                        <TBtn data-test-timeline-today onClick={this.goToCurrentWeek}>Today</TBtn>
                        <TBtn data-test-timeline-next-week onClick={this.nextWeek}>›</TBtn>
                        <WeekLabel>{this.currentWeekLabel}</WeekLabel>
                        <span style={{ flex: 1 }} />
                        {this.renderAddButton()}
                        <TBtn onClick={this.zoomOut}>−</TBtn>
                        <ZoomLabel>{this.zoomLabel}</ZoomLabel>
                        <TBtn onClick={this.zoomIn}>+</TBtn>
                        <TBtn data-test-timeline-scroll-today onClick={this.scrollToToday}>Scroll to Today</TBtn>
                    </TimelineToolbar>

                    {/* Timeline body: labels + scrollable area */}
                    <TimelineBody>
                        {/* Left label column (groups + items) */}
                        <LabelColumn>
                            {groups.length === 0 && (
                                <LoadingMsg>No data to display</LoadingMsg>
                            )}
                            {groups.map(group => (
                                <React.Fragment key={group.id}>
                                    <GroupHeader
                                        data-test-timeline-group={group.id}
                                        clickable={!!this.onLabelDoubleClick}
                                        onDoubleClick={() => { if (this.onLabelDoubleClick) this.onLabelDoubleClick(group); }}
                                    >
                                        <GroupHeaderFlex>
                                            <span>{group.label} ({group.items.length})</span>
                                            {this.renderGroupEditButton(group)}
                                        </GroupHeaderFlex>
                                    </GroupHeader>
                                    {group.items.map((item: any) => {
                                        const id = item.id ?? item.cid;
                                        return (
                                            <LabelRow
                                                key={id}
                                                data-test-timeline-label={id}
                                                onDoubleClick={() => this.onBarDoubleClick(item)}
                                            >
                                                <LabelColor style={{ background: this.barColor(item) }} />
                                                <LabelText>{this.getBarLabel(item)}</LabelText>
                                            </LabelRow>
                                        );
                                    })}
                                </React.Fragment>
                            ))}
                        </LabelColumn>

                        {/* Right scrollable timeline */}
                        <TimelineScroll id="timeline-scroll-area">
                            {/* Date headers */}
                            <TimelineDates>
                                {dates.map((date, i) => (
                                    <DateCell
                                        key={i}
                                        weekend={isWeekend(date)}
                                        style={{ width }}
                                    >
                                        <DateMonth>{date.toLocaleDateString('en-US', { month: 'short' })}</DateMonth>
                                        <DateDay today={isToday(date)}>{date.getDate()}</DateDay>
                                    </DateCell>
                                ))}
                            </TimelineDates>

                            {/* Grid lines */}
                            <TimelineGrid style={{ width: totalWidth }}>
                                {dates.map((date, i) => (
                                    <GridLine
                                        key={i}
                                        style={{
                                            left: i * width,
                                            background: isWeekend(date) ? COLORS.border : 'transparent',
                                        }}
                                    />
                                ))}
                                {/* Today line */}
                                {dates.some(isToday) && (
                                    <TodayLine style={{ left: daysBetween(this.startDate, new Date()) * width }} />
                                )}
                            </TimelineGrid>

                            {/* Group tracks with bars */}
                            {groups.map(group => (
                                <GroupTrack key={group.id}>
                                    <TrackLabel>{group.label}</TrackLabel>
                                    {group.items.map((item: any) => {
                                        const id = item.id ?? item.cid;
                                        const barStart = this.getBarStart(item);
                                        const barEnd = this.getBarEnd(item);
                                        const left = barStart ? daysBetween(this.startDate, barStart) * width : 0;
                                        const duration = barStart && barEnd ? Math.max(daysBetween(barStart, barEnd), 1) : 5;
                                        const barWidth = Math.max(duration * width, 60);
                                        return (
                                            <BarRow key={id}>
                                                <Bar
                                                    data-test-timeline-bar={id}
                                                    style={{
                                                        left,
                                                        width: barWidth,
                                                        background: this.barColor(item),
                                                    }}
                                                    onClick={() => this.onBarClick(item)}
                                                    onDoubleClick={() => this.onBarDoubleClick(item)}
                                                >
                                                    {this.renderTimelineBar(item) || this.getBarLabel(item)}
                                                    {this.renderBarEditButton(item)}
                                                </Bar>
                                            </BarRow>
                                        );
                                    })}
                                </GroupTrack>
                            ))}
                        </TimelineScroll>
                    </TimelineBody>
                    {/* Side panel (if any) — inside wrapper so absolute positioning works */}
                    {this.renderSidePanel()}
                </TimelineWrapper>
                {/* Keep AdminOverview sidebars (filters, my-filters) */}
                {this.renderSidebars()}
                {/* Edit modal (if any) */}
                {this.renderEditModal()}
            </React.Fragment>
        );
    }

    // Default bar label — can be overridden
    getBarLabel(item: any): string {
        return item.name || item.title || `#${item.id}`;
    }
}
